import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import { signalingRoomJoined } from 'features/room/actions';
import { PhoneStream, SIPState } from 'features/sip/types';
import {
  phoneStreamDeleted,
  signalingPhoneBridgeConnected,
  signalingPhoneBridgeConnecting,
  signalingPhoneBridgeDisconnected,
  signalingPhoneUserAskedToUnmute,
  signalingPhoneUserHandLowered,
  signalingPhoneUserHandRaised,
  signalingPhoneUserMuted,
  signalingPhoneUsersJoined,
  signalingPhoneUsersLeft,
  signalingPhoneUserUnmuted,
} from 'features/sip/actions';
import { normalizeTelephonyUsers } from 'features/sip/utils/normalizeTelephonyUsers';
import { getEntriesByIds } from 'utils/getEntriesByIds';
import { UserId } from 'features/users/types';

export const initialState: SIPState = {
  status: 'disconnected',
  entities: {},
  ids: [],
  handRaisedIds: [],
  phoneStream: null,
};

export const sipSlice = createSlice({
  name: 'sip',
  initialState,
  reducers: {
    phoneStreamUpdated(state, action: PayloadAction<PhoneStream>) {
      state.phoneStream = action.payload;
    },
    phoneHandRaiseDismissed(state, action: PayloadAction<UserId>) {
      const user = state.entities[action.payload];
      user.handRaised = false;
      state.handRaisedIds = state.handRaisedIds.filter((id) => id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signalingRoomJoined, (state, action) => {
        const { telephony } = action.payload;
        if (telephony) {
          state.status = telephony.status;

          const { ids, entities } = normalizeTelephonyUsers(telephony.users);

          state.ids = ids;
          state.entities = entities;

          for (const user of telephony.users) {
            if (user.handRaised) {
              state.handRaisedIds.push(user.id);
            }
          }
        }
      })
      .addCase(phoneStreamDeleted, (state) => {
        state.phoneStream = null;
      })
      .addCase(signalingPhoneBridgeConnecting, (state) => {
        state.status = 'connecting';
      })
      .addCase(signalingPhoneBridgeConnected, (state) => {
        state.status = 'connected';
      })
      .addCase(signalingPhoneBridgeDisconnected, (state) => {
        state.status = 'disconnected';
        state.ids = [];
        state.entities = {};
        state.handRaisedIds = [];
      })
      .addCase(signalingPhoneUsersLeft, (state, action) => {
        const idsSet = new Set(state.ids);
        const handRaisedIdsSet = new Set(state.handRaisedIds);

        for (const userId of action.payload.users) {
          idsSet.delete(userId);
          handRaisedIdsSet.delete(userId);
          delete state.entities[userId];
        }

        state.ids = Array.from(idsSet);
        state.handRaisedIds = Array.from(handRaisedIdsSet);
      })
      .addCase(signalingPhoneUsersJoined, (state, action) => {
        for (const user of action.payload.users) {
          state.entities[user.id] = user;
          state.ids.push(user.id);
        }
        state.ids = [...new Set(state.ids)];
      })
      .addCase(signalingPhoneUserMuted, (state, action) => {
        const user = state.entities[action.payload.id];
        if (user) {
          user.muted = true;
        }
      })
      .addCase(signalingPhoneUserUnmuted, (state, action) => {
        const user = state.entities[action.payload.id];
        if (user) {
          user.muted = false;
          user.askedToUnmute = false;
        }
      })
      .addCase(signalingPhoneUserHandRaised, (state, action) => {
        const { id } = action.payload.initiator;
        const user = state.entities[id];

        if (user) {
          user.handRaised = true;
          state.handRaisedIds.push(id);
        }
      })
      .addCase(signalingPhoneUserHandLowered, (state, action) => {
        const user = state.entities[action.payload.id];
        if (user) {
          user.handRaised = false;
        }
        state.handRaisedIds = state.handRaisedIds.filter((id) => id !== action.payload.id);
      })
      .addCase(signalingPhoneUserAskedToUnmute, (state, action) => {
        const { data } = action.payload;
        if (data) {
          const user = state.entities[data.id];
          if (user) {
            user.askedToUnmute = true;
          }
        }
      });
  },
});

export const { phoneStreamUpdated, phoneHandRaiseDismissed } = sipSlice.actions;

export const selectSIPUserIds = (state: RootState) => state.sip.ids;
export const selectSIPUserEntities = (state: RootState) => state.sip.entities;
export const selectSIPHandRaisedIds = (state: RootState) => state.sip.handRaisedIds;

export const selectSIPStatus = (state: RootState) => state.sip.status;
export const selectPhoneStream = (state: RootState) => state.sip.phoneStream;

export const selectPhoneUserById = (state: RootState, id: UserId) => {
  const user = state.sip.entities[id];
  if (!user) {
    return undefined;
  }

  return user;
};

export const selectPhoneUserBroadcasting = createSelector(
  [(state, id) => selectPhoneUserById(state, id)],
  (user) => {
    if (!user) {
      return false;
    }

    return !user.muted;
  }
);

export const selectSIPUsers = createSelector(
  [selectSIPUserIds, selectSIPUserEntities],
  (ids, entities) => getEntriesByIds(ids, entities)
);

export default sipSlice.reducer;
